import React, { useState, useEffect } from 'react';
import { Video, Phone, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const SolicitarVideollamada = () => {
  const [step, setStep] = useState('form'); // form, creating, ready
  const [formData, setFormData] = useState({
    nombre: '',
    motivo: ''
  });
  const [jitsiData, setJitsiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/public/solicitar-videollamada`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setJitsiData(data);
        setStep('ready');
      } else {
        setError(data.detail || 'Error al crear la videollamada');
      }
    } catch (err) {
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCall = () => {
    if (jitsiData?.jitsi_url) {
      window.open(jitsiData.jitsi_url, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Video className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-bold">Solicitar Videollamada</h1>
          <p className="text-purple-200 mt-1">Instituto Bíblico Apostólico</p>
        </div>

        <div className="p-6">
          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-blue-800 mb-2">
                  <Clock className="h-4 w-4" />
                  <span className="font-medium">Horario de Atención</span>
                </div>
                <p className="text-sm text-blue-700">
                  11:00 AM - 10:00 PM (Hora de Uruguay)
                </p>
              </div>

              <div>
                <Label htmlFor="nombre">Tu nombre</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ingresa tu nombre"
                  required
                />
              </div>

              <div>
                <Label htmlFor="motivo">¿En qué podemos ayudarte?</Label>
                <Textarea
                  id="motivo"
                  value={formData.motivo}
                  onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                  placeholder="Cuéntanos brevemente el motivo de tu consulta..."
                  rows={3}
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-lg py-6"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Creando sala...
                  </>
                ) : (
                  <>
                    <Video className="h-5 w-5 mr-2" />
                    Solicitar Videollamada
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Al solicitar, el Pastor Luis R. Rodriguez recibirá una notificación
              </p>
            </form>
          )}

          {step === 'ready' && jitsiData && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>

              <div>
                <h2 className="text-xl font-bold text-gray-900">¡Sala Lista!</h2>
                <p className="text-gray-600 mt-2">
                  {jitsiData.mensaje || 'Tu sala de videollamada está lista. El Pastor recibirá una notificación.'}
                </p>
              </div>

              {jitsiData.fuera_de_horario && (
                <div className="bg-yellow-50 rounded-lg p-4 text-left">
                  <p className="text-yellow-800 text-sm">
                    ⚠️ Estás fuera del horario de atención. Puedes esperar en la sala, pero es posible que no haya respuesta inmediata.
                  </p>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-2">Sala de videollamada:</p>
                <p className="font-mono text-sm text-gray-700 break-all">{jitsiData.sala_id}</p>
              </div>

              <Button 
                onClick={handleJoinCall}
                className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
              >
                <Phone className="h-5 w-5 mr-2" />
                UNIRSE A LA VIDEOLLAMADA
              </Button>

              <p className="text-xs text-gray-500">
                Se abrirá en una nueva pestaña. Permite el acceso a cámara y micrófono.
              </p>

              <button 
                onClick={() => { setStep('form'); setJitsiData(null); }}
                className="text-purple-600 hover:underline text-sm"
              >
                ← Volver al inicio
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 text-center text-xs text-gray-500">
          Instituto Bíblico Apostólico Uruguay
        </div>
      </div>
    </div>
  );
};

export default SolicitarVideollamada;
